import {
    MutableRefObject,
    useLayoutEffect,
    useRef,
} from 'react';

export type DockedAnnotationViewportEventType = 'resize' | 'scroll';

const VIEWPORT_ROOT_POST_COMMIT_SYNC_FRAMES = 2;
const VIEWPORT_ROOT_CAPTURE_SETTLE_MS = 120;
const MIN_KEYBOARD_SAFE_VIEWPORT_OCCLUSION_PX = 80;

export interface DockedAnnotationViewportDriver {
    addEventListener: (
        type: DockedAnnotationViewportEventType,
        listener: () => void,
    ) => void,
    removeEventListener: (
        type: DockedAnnotationViewportEventType,
        listener: () => void,
    ) => void,
    resolveLiveFrame: (layoutViewportHeight: number) => {
        top: number,
        height: number,
    },
}

/**
 * Converts the live visual viewport frame into inline bounds for the docked
 * mobile portal root. Once the keyboard-safe height has been captured, keep
 * using it so touch-scroll viewport noise cannot move the docked editor while
 * still anchoring the root to the live document scroll position.
 */
const resolveViewportRootFrame = (
    capturedHeight: number,
    scrollTop: number,
    liveFrame: {
        top: number,
        height: number,
    },
): {
    top: number,
    height: number,
} => ({
    top: Math.max(scrollTop, 0),
    height: capturedHeight > 0 ? capturedHeight : liveFrame.height,
});

/**
 * Mirrors the visible viewport into a document-anchored overlay root and
 * freezes the first keyboard-safe height it sees after open so the dock does
 * not keep re-capturing during the first touch scroll.
 */
export const useDockedAnnotationViewportRoot = ({
    enabled,
    viewportDriver,
}: {
    enabled: boolean,
    viewportDriver?: DockedAnnotationViewportDriver,
}): MutableRefObject<HTMLDivElement | null> => {
    const dockedAnnotationViewportRootRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const dockedAnnotationViewportRoot = dockedAnnotationViewportRootRef.current;
        if (!dockedAnnotationViewportRoot) {
            return undefined;
        }

        if (!enabled) {
            dockedAnnotationViewportRoot.style.top = '';
            dockedAnnotationViewportRoot.style.bottom = '';
            dockedAnnotationViewportRoot.style.height = '';
            return undefined;
        }

        const resolvedViewportDriver = viewportDriver || (
            window.visualViewport
                ? {
                    addEventListener: (
                        type: DockedAnnotationViewportEventType,
                        listener: () => void,
                    ) => window.visualViewport?.addEventListener(type, listener),
                    removeEventListener: (
                        type: DockedAnnotationViewportEventType,
                        listener: () => void,
                    ) => window.visualViewport?.removeEventListener(type, listener),
                    resolveLiveFrame: () => ({
                        top: window.visualViewport?.offsetTop || 0,
                        height: window.visualViewport?.height || 0,
                    }),
                }
                : null
        );

        if (!resolvedViewportDriver) {
            dockedAnnotationViewportRoot.style.top = '';
            dockedAnnotationViewportRoot.style.bottom = '';
            dockedAnnotationViewportRoot.style.height = '';
            return undefined;
        }

        let capturedViewportHeight = 0;
        let largestObservedViewportHeight = 0;
        let viewportHeightCaptureFrozen = false;
        let viewportHeightCaptureSettleTimeoutId: number | null = null;

        const hasCapturedKeyboardSafeViewportHeight = (): boolean => (
            largestObservedViewportHeight > 0
            && (largestObservedViewportHeight - capturedViewportHeight) >= MIN_KEYBOARD_SAFE_VIEWPORT_OCCLUSION_PX
        );

        const captureViewportHeight = (liveHeight: number): void => {
            if (viewportHeightCaptureFrozen || liveHeight <= 0) {
                return;
            }

            largestObservedViewportHeight = Math.max(
                largestObservedViewportHeight,
                liveHeight,
            );
            capturedViewportHeight = capturedViewportHeight > 0
                ? Math.min(capturedViewportHeight, liveHeight)
                : liveHeight;

            const keyboardOcclusion = Math.max(
                largestObservedViewportHeight - liveHeight,
                0,
            );
            if (keyboardOcclusion >= MIN_KEYBOARD_SAFE_VIEWPORT_OCCLUSION_PX) {
                if (viewportHeightCaptureSettleTimeoutId != null) {
                    window.clearTimeout(viewportHeightCaptureSettleTimeoutId);
                }
                viewportHeightCaptureSettleTimeoutId = window.setTimeout(() => {
                    viewportHeightCaptureSettleTimeoutId = null;
                    viewportHeightCaptureFrozen = true;
                    syncViewportRootFrame();
                }, VIEWPORT_ROOT_CAPTURE_SETTLE_MS);
            }
        };

        const readViewportRootFrame = (): {
            top: number,
            height: number,
        } => {
            const layoutViewportHeight = Math.max(
                window.innerHeight || 0,
                document.documentElement.clientHeight || 0,
            );
            const liveFrame = resolvedViewportDriver.resolveLiveFrame(
                layoutViewportHeight,
            );
            const scrollTop = Math.max(
                window.scrollY || 0,
                document.documentElement.scrollTop || 0,
            );
            captureViewportHeight(liveFrame.height);

            return resolveViewportRootFrame(
                capturedViewportHeight,
                scrollTop,
                liveFrame,
            );
        };

        const applyViewportRootFrame = (frame: {
            top: number,
            height: number,
        }): void => {
            dockedAnnotationViewportRoot.style.top = `${frame.top}px`;
            dockedAnnotationViewportRoot.style.bottom = '';
            dockedAnnotationViewportRoot.style.height = `${frame.height}px`;
        };

        const syncViewportRootFrame = (): void => {
            applyViewportRootFrame(readViewportRootFrame());
        };

        /**
         * Highlight reveal can scroll before the software keyboard finishes
         * opening. Only freeze the captured height once a real keyboard-safe
         * frame already exists, so reveal scroll does not lock the root too
         * early while user scroll still cannot re-open capture later.
         */
        const syncViewportRootFrameOnWindowScroll = (): void => {
            syncViewportRootFrame();
            if (!hasCapturedKeyboardSafeViewportHeight()) {
                return;
            }

            viewportHeightCaptureFrozen = true;
            if (viewportHeightCaptureSettleTimeoutId != null) {
                window.clearTimeout(viewportHeightCaptureSettleTimeoutId);
                viewportHeightCaptureSettleTimeoutId = null;
            }
        };

        /**
         * Safari can deliver the final visible-viewport frame a beat after the
         * editor first opens and reveals the highlight. Run a short post-commit
         * resync so the dock does not declare itself ready against a stale
         * layout-viewport-aligned frame.
         */
        let postCommitSyncRafId: number | null = null;
        const schedulePostCommitSync = (remainingFrames: number): void => {
            if (remainingFrames <= 0) {
                return;
            }

            postCommitSyncRafId = window.requestAnimationFrame(() => {
                syncViewportRootFrame();
                schedulePostCommitSync(remainingFrames - 1);
            });
        };

        syncViewportRootFrame();
        schedulePostCommitSync(VIEWPORT_ROOT_POST_COMMIT_SYNC_FRAMES);
        resolvedViewportDriver.addEventListener('resize', syncViewportRootFrame);
        resolvedViewportDriver.addEventListener('scroll', syncViewportRootFrame);
        window.addEventListener('scroll', syncViewportRootFrameOnWindowScroll, { passive: true });

        return () => {
            if (postCommitSyncRafId != null) {
                window.cancelAnimationFrame(postCommitSyncRafId);
            }
            if (viewportHeightCaptureSettleTimeoutId != null) {
                window.clearTimeout(viewportHeightCaptureSettleTimeoutId);
            }
            resolvedViewportDriver.removeEventListener('resize', syncViewportRootFrame);
            resolvedViewportDriver.removeEventListener('scroll', syncViewportRootFrame);
            window.removeEventListener('scroll', syncViewportRootFrameOnWindowScroll);
            if (!dockedAnnotationViewportRootRef.current) {
                return;
            }

            dockedAnnotationViewportRootRef.current.style.top = '';
            dockedAnnotationViewportRootRef.current.style.bottom = '';
            dockedAnnotationViewportRootRef.current.style.height = '';
        };
    }, [enabled, viewportDriver]);

    return dockedAnnotationViewportRootRef;
};
